import {
    CameraServiceType,
    GeoZones,
    GeoZoneValidationType,
    PowerSupply,
    PurposeType,
    Solar4gCamera,
    SolarPanelPositionType,
    Status,
} from '@EcamModel/model';
import { CameraNotes } from '@EcamModel/model/CameraNotes';
import { CameraTask } from '@EcamModel/model/CameraTask';
import {
    CameraTrailAuditEntity,
    CameraTrailAuditEvent,
    CameraTrailAuditEventEntityName,
    CameraTrailAuditEventType,
    GeoZoneAuditEvent,
} from '@EcamModel/model/CameraTrailAudit';
import { PTZPatrolMode } from '@EcamModel/model/PTZCamera';
import { Box, Stack, TableCell, Typography } from '@mui/material';
import { positionSolarPanelReadableMapping } from '@pages/cameras/add-new';
import { formatPermitLength } from '@pages/cameras/utils/EvPermitHelpers';
import {
    cameraPowerSuppliesReadableMapping,
    patrolModeReadableMapping,
    purposesReadableMapping,
} from '@pages/cameras/list/components/CameraItem';
import { cameraServiceMapping, cameraStatusMapping } from '@pages/camerasOverview/configs';
import { getValueFromMapping } from '@pages/components';
import { CameraSubTasks, mappingPriorityTask, mappingStatusTask } from '@pages/task-management/config';
import { formatEnumValue } from '@pages/verification/pc-details/components/recordedPCNEventLog/helpers';
import color from '@theme/Colors';
import _ from 'lodash';
import { Fragment } from 'react';
import { formatISODate, formatISODateTime, formatUKDate, formatUKDateTime } from 'src/helpers';
import GracePeriodConfig from '../components/GracePeriodConfig';
import EvPermitZonesChange from '../components/EvPermitZonesChange';
import { checkValues, detectModesMapping, eventLogType, keyCameraTrailAuditEntity, Section } from '../configs';

const solar4gCellularStatusKeys = new Set<string>([
    'ISP',
    'Connection status',
    'SIM status',
    'Signal strength',
    'IMEI',
    'IMSI',
    'ICCID',
    'IP address',
    'Subnet mask',
    'Gateway',
    'DNS',
    'Monthly data',
]);
const solar4gCellularInfoKeys = new Set<string>(['APN', 'Username', 'Password']);

// Display label of EvPermitZoneNames (see keyCameraTrailAuditEntity) — rendered as an added/removed diff
const EV_PERMIT_ZONES_LABEL = 'EV permit zones';

const solar4gCaptureInfoKeys = new Set<string>([
    'Snapshot topic',
    'Status topic',
    'Alarm topic',
    'Configure request topic',
    'Configure respond topic',
]);

const solar4gDuplicatedLabelKeys = new Set<string>(['Gateway', 'DNS', 'IP address']);

const getSolar4gChangeLabel = (solar4gKey: string, parentKey: string): string => {
    const baseLabel = keyCameraTrailAuditEntity[solar4gKey as keyof typeof keyCameraTrailAuditEntity] || solar4gKey;

    if (solar4gDuplicatedLabelKeys.has(baseLabel)) {
        const suffix = parentKey === 'Solar4gCellularInfo' ? ' (info)' : ' (status)';
        return `${baseLabel}${suffix}`;
    }

    return baseLabel;
};

export const booleanToString = (value: boolean): string => (value ? 'On' : 'Off');

export const formatCommaSeparatedValues = (value: unknown): string => {
    if (_.isNil(value) || value === '' || value === 'N/A') return (value as string) ?? '';

    const items = Array.isArray(value) ? value.map((item) => String(item)) : String(value).split(',');

    return items
        .map((item) => item.trim())
        .filter((item) => item.length > 0)
        .map((item) => _.upperFirst(item))
        .join(', ');
};

export const positionPanelToString = (positionPanel: SolarPanelPositionType): string => {
    return positionSolarPanelReadableMapping[positionPanel]?.label || 'N/A';
};

export const calculateChanges = (
    oldData: CameraTrailAuditEntity | null,
    newData: CameraTrailAuditEntity | null
): checkValues[] => {
    const changesValue: checkValues[] = [];

    oldData = oldData || {};
    newData = newData || {};

    const allKeys = Array.from(new Set([...Object.keys(oldData), ...Object.keys(newData)]));

    for (const key of allKeys) {
        if (newData[key] !== oldData[key]) {
            if (key === 'Solar4gCellularStatus' || key === 'Solar4gCellularInfo' || key === 'Solar4gCaptureInfo') {
                const newSolar4gData = (newData[key] ?? {}) as Record<string, unknown>;
                const solar4gKeys = Array.from(new Set([...Object.keys(newSolar4gData)]));

                for (const solar4gKey of solar4gKeys) {
                    const newSolar4gValue = newSolar4gData[solar4gKey];
                    const normalizedNewValue = _.isNil(newSolar4gValue) ? 'N/A' : newSolar4gValue;

                    const renamedSolar4gKey = getSolar4gChangeLabel(solar4gKey, key);

                    changesValue.push({
                        key: renamedSolar4gKey,
                        oldValue: '',
                        newValue: String(normalizedNewValue),
                    });
                }

                continue;
            }

            let oldValue = oldData[key] !== undefined ? oldData[key] : 'N/A';
            let newValue = newData[key] !== undefined ? newData[key] : 'N/A';

            if (key === 'Status') {
                oldValue = getValueFromMapping(oldValue as Status, cameraStatusMapping);
                newValue = getValueFromMapping(newValue as Status, cameraStatusMapping);
            }

            if (key === 'ValidationType') {
                oldValue = getValueFromMapping(oldValue as GeoZoneValidationType, detectModesMapping);
                newValue = getValueFromMapping(newValue as GeoZoneValidationType, detectModesMapping);
            }

            if (key === 'ServiceType') {
                oldValue = getValueFromMapping(oldValue as CameraServiceType, cameraServiceMapping);
                newValue = getValueFromMapping(newValue as CameraServiceType, cameraServiceMapping);
            }

            if (key === 'PurposeType') {
                oldValue = getValueFromMapping(oldValue as PurposeType, purposesReadableMapping);
                newValue = getValueFromMapping(newValue as PurposeType, purposesReadableMapping);
            }

            if (key === 'DeployedDate') {
                oldValue = formatISODate(oldValue as Date);
                newValue = formatISODate(newValue as Date);
            }

            if (key === 'IsVerificationsEnabled') {
                oldValue = booleanToString(oldValue as boolean);
                newValue = booleanToString(newValue as boolean);
            }

            if (key === 'PermitExemptionsEnabled') {
                oldValue = booleanToString(oldValue as boolean);
                newValue = booleanToString(newValue as boolean);
            }

            if (key === 'PostCaptureNumber') {
                oldValue = booleanToString(oldValue as boolean);
                newValue = booleanToString(newValue as boolean);
            }

            if (key === 'EnablePcnVerificationAt') {
                oldValue = oldValue === null ? 'N/A' : formatUKDateTime(oldValue);
                newValue = newValue === null ? 'N/A' : formatUKDateTime(newValue);
            }
            if (key === 'SolarPanelPosition') {
                oldValue = positionPanelToString(oldValue as SolarPanelPositionType);
                newValue = positionPanelToString(newValue as SolarPanelPositionType);
            }

            if (key === 'TaskStatus') {
                oldValue = mappingStatusTask[oldValue];
                newValue = mappingStatusTask[newValue];
            }
            if (key === 'TaskPriority') {
                oldValue = mappingPriorityTask[oldValue];
                newValue = mappingPriorityTask[newValue];
            }
            if (key === 'EndDate') {
                oldValue = formatISODateTime(oldValue as Date);
                newValue = formatISODateTime(newValue as Date);
            }

            if (key === 'PowerSupply') {
                oldValue = getValueFromMapping(oldValue as PowerSupply, cameraPowerSuppliesReadableMapping);
                newValue = getValueFromMapping(newValue as PowerSupply, cameraPowerSuppliesReadableMapping);
            }

            if (key === 'EnableGroupMatchingAt') {
                oldValue = oldValue === null ? 'N/A' : formatUKDateTime(oldValue);
                newValue = newValue === null ? 'N/A' : formatUKDateTime(newValue);
                // Add a key not in Camera model
                const displayOld = oldData[key] === null ? 'Off' : 'On';
                const displayNew = newData[key] === null ? 'Off' : 'On';
                changesValue.push({
                    key: 'Enable group matching',
                    oldValue: displayOld,
                    newValue: displayNew,
                });
            }

            if (key === 'PatrolMode') {
                oldValue = getValueFromMapping(oldValue as PTZPatrolMode, patrolModeReadableMapping);
                newValue = getValueFromMapping(newValue as PTZPatrolMode, patrolModeReadableMapping);
            }

            if (key === 'IsSendEVPermitEnabled') {
                oldValue = booleanToString(oldValue as boolean);
                newValue = booleanToString(newValue as boolean);
            }

            if (key === 'EvPermitLengthSeconds') {
                oldValue = formatPermitLength(oldValue as number | null);
                newValue = formatPermitLength(newValue as number | null);
            }

            const renamedKey = keyCameraTrailAuditEntity[key] || key;

            changesValue.push({
                key: renamedKey,
                oldValue,
                newValue,
            });
        }
    }

    return changesValue;
};

export const keyExistsInList = (keys: string[], changes?: checkValues[]): boolean => {
    return changes ? keys.some((key) => changes.some((change) => change.key === key)) : false;
};

export const renderChangeItem = (change: checkValues, item: CameraTrailAuditEvent) => {
    const isEffectiveFrom =
        change.key === 'Effective from' &&
        ((item?.NewValues as GeoZoneAuditEvent)?.EffectiveFrom ||
            (item?.OldValues as GeoZoneAuditEvent)?.EffectiveFrom);

    const isContraventionType =
        change.key === 'Contravention type' &&
        ((item?.NewValues as GeoZoneAuditEvent)?.ContraventionType ||
            (item?.OldValues as GeoZoneAuditEvent)?.ContraventionType);

    const isGeoZoneCoordinates =
        change.key === 'Coordinates' &&
        ((item?.NewValues as GeoZoneAuditEvent)?.GeoZoneCoordinates ||
            (item?.OldValues as GeoZoneAuditEvent)?.GeoZoneCoordinates);

    const isGeoZoneStatus =
        change.key === 'Status' &&
        ((item?.NewValues as GeoZoneAuditEvent)?.Status || (item?.OldValues as GeoZoneAuditEvent)?.Status) &&
        item.EntityName === CameraTrailAuditEventEntityName.geoZones;

    const isGeoZoneValidationType =
        change.key === 'Validation type' && item.EntityName === CameraTrailAuditEventEntityName.geoZones;
    const isGeoZonePermitExceptions =
        change.key === 'Permit exceptions' && item.EntityName === CameraTrailAuditEventEntityName.geoZones;

    const isGZOConfigs =
        change.key === 'Geo-zone configs' &&
        ((item?.NewValues as GeoZoneAuditEvent)?.GZOConfigs || (item?.OldValues as GeoZoneAuditEvent)?.GZOConfigs) &&
        item.EntityName === CameraTrailAuditEventEntityName.geoZones;

    const isTaskType =
        change.key === 'Task Type' &&
        ((item?.NewValues as CameraTask)?.TaskType || (item?.OldValues as CameraTask)?.TaskType) &&
        item.EntityName === CameraTrailAuditEventEntityName.cameraTask;

    const isTaskTag =
        change.key === 'Sub task' &&
        ((item?.NewValues as CameraTask)?.SubTask || (item?.OldValues as CameraTask)?.SubTask) &&
        item.EntityName === CameraTrailAuditEventEntityName.cameraTask;

    const isSolar4gCellularInfo =
        solar4gCellularInfoKeys.has(change.key) &&
        item.EntityName === CameraTrailAuditEventEntityName.solar4gCamera &&
        (item?.NewValues as Solar4gCamera).Solar4gCellularInfo !== null;

    const isSolar4gCellularStatus =
        solar4gCellularStatusKeys.has(change.key) &&
        item.EntityName === CameraTrailAuditEventEntityName.solar4gCamera &&
        (item?.NewValues as Solar4gCamera).Solar4gCellularStatus !== null;

    const isSolar4gCaptureInfo =
        solar4gCaptureInfoKeys.has(change.key) &&
        item.EntityName === CameraTrailAuditEventEntityName.solar4gCamera &&
        (item?.NewValues as Solar4gCamera).Solar4gCellularStatus !== null;

    const isExemptVehicleTypes =
        change.key === 'Exempt vehicle types' &&
        ((item?.NewValues as CameraNotes)?.ExemptVehicleTypes ||
            (item?.OldValues as CameraNotes)?.ExemptVehicleTypes) &&
        item.EntityName === CameraTrailAuditEventEntityName.notes;

    return (
        <Box key={`${change.key}`}>
            {isEffectiveFrom ? (
                <ul
                    style={{
                        marginTop: '4px',
                        marginBottom: '4px',
                    }}
                >
                    <li>
                        <Typography>
                            {change.key}:{' '}
                            {formatUKDate(
                                (item?.NewValues as GeoZoneAuditEvent)?.EffectiveFrom! ||
                                    (item?.OldValues as GeoZoneAuditEvent)?.EffectiveFrom
                            )}
                        </Typography>
                    </li>
                </ul>
            ) : isGeoZoneStatus ? (
                <ul
                    style={{
                        marginTop: '4px',
                        marginBottom: '4px',
                    }}
                >
                    <li>
                        {item?.AuditEventType === CameraTrailAuditEventType.UPDATE ? (
                            <Typography>
                                {`${change.key}: from “${
                                    cameraStatusMapping[(item?.OldValues as GeoZoneAuditEvent)?.Status!]
                                }” to “${cameraStatusMapping[(item?.NewValues as GeoZoneAuditEvent)?.Status!]}”`}
                            </Typography>
                        ) : (
                            <Typography>
                                {change.key}:{' '}
                                {
                                    cameraStatusMapping[
                                        (item?.NewValues as GeoZoneAuditEvent)?.Status! ||
                                            (item?.OldValues as GeoZoneAuditEvent)?.Status!
                                    ]
                                }
                            </Typography>
                        )}
                    </li>
                </ul>
            ) : isContraventionType ? (
                <ul
                    style={{
                        marginTop: '4px',
                        marginBottom: '4px',
                    }}
                >
                    <li>
                        {item?.AuditEventType === CameraTrailAuditEventType.UPDATE ? (
                            <Typography>
                                {`${change.key}: from “${
                                    (item?.OldValues as GeoZoneAuditEvent)?.ContraventionType ?? ''
                                }” to “${(item?.NewValues as GeoZoneAuditEvent)?.ContraventionType ?? ''}”`}
                            </Typography>
                        ) : (
                            <Typography>
                                {change.key}:{' '}
                                {(item?.NewValues as GeoZoneAuditEvent)?.ContraventionType ||
                                    (item?.OldValues as GeoZoneAuditEvent)?.ContraventionType}
                            </Typography>
                        )}
                    </li>
                </ul>
            ) : isGeoZoneCoordinates ? (
                <ul
                    style={{
                        marginTop: '4px',
                        marginBottom: '4px',
                    }}
                >
                    <li>
                        <Typography>
                            {`${change.key}:`}
                            <ul
                                style={{
                                    marginTop: '4px',
                                    marginBottom: '4px',
                                }}
                            >
                                {['X1', 'X2', 'X3', 'X4'].map((axis, index) => {
                                    const yAxis = `Y${axis[1]}`;

                                    const oldValueCoordinatesX = (item?.OldValues as GeoZoneAuditEvent)
                                        ?.GeoZoneCoordinates?.[axis];
                                    const newValueCoordinatesX = (item?.NewValues as GeoZoneAuditEvent)
                                        ?.GeoZoneCoordinates?.[axis];

                                    const oldValueCoordinatesY = (item?.OldValues as GeoZoneAuditEvent)
                                        ?.GeoZoneCoordinates?.[yAxis];
                                    const newValueCoordinatesY = (item?.NewValues as GeoZoneAuditEvent)
                                        ?.GeoZoneCoordinates?.[yAxis];

                                    return item.AuditEventType === CameraTrailAuditEventType.UPDATE ? (
                                        <li key={axis}>
                                            {`From "[${axis} = ${Number(oldValueCoordinatesX)}, Y${axis[1]} = ${Number(
                                                oldValueCoordinatesY
                                            )}]" to "[${axis} = ${Number(newValueCoordinatesX)}, Y${axis[1]} = ${Number(
                                                newValueCoordinatesY
                                            )}]"`}
                                        </li>
                                    ) : (
                                        <li key={axis}>
                                            {`"[${axis} = ${Number(oldValueCoordinatesX || newValueCoordinatesX)}, Y${
                                                axis[1]
                                            } = ${Number(oldValueCoordinatesY || newValueCoordinatesY)}]"`}
                                        </li>
                                    );
                                })}
                            </ul>
                        </Typography>
                    </li>
                </ul>
            ) : isGZOConfigs && !isGeoZoneValidationType && !isGeoZonePermitExceptions ? (
                <ul
                    style={{
                        marginTop: '4px',
                        marginBottom: '4px',
                    }}
                >
                    {item.AuditEventType !== CameraTrailAuditEventType.UPDATE && (
                        <GracePeriodConfig
                            change={change}
                            configValues={
                                (item?.NewValues as GeoZoneAuditEvent)?.GZOConfigs ||
                                (item?.OldValues as GeoZoneAuditEvent)?.GZOConfigs
                            }
                        />
                    )}
                </ul>
            ) : isTaskType ? (
                <ul
                    style={{
                        marginTop: '4px',
                        marginBottom: '4px',
                    }}
                >
                    <li>
                        <Typography>
                            {`${change.key}: ${(() => {
                                const safeParse = (value: any) => {
                                    try {
                                        return JSON.parse(value);
                                    } catch {
                                        return value;
                                    }
                                };

                                try {
                                    const oldVal = safeParse(change.oldValue);
                                    const newVal = safeParse(change.newValue);

                                    return item.AuditEventType === CameraTrailAuditEventType.UPDATE
                                        ? `from "${Array.isArray(oldVal) ? oldVal.join(', ') : oldVal}" to "${
                                              Array.isArray(newVal) ? newVal.join(', ') : newVal
                                          }"`
                                        : `"${
                                              Array.isArray(
                                                  item.AuditEventType === CameraTrailAuditEventType.CREATE
                                                      ? newVal
                                                      : oldVal
                                              )
                                                  ? (item.AuditEventType === CameraTrailAuditEventType.CREATE
                                                        ? newVal
                                                        : oldVal
                                                    ).join(', ')
                                                  : item.AuditEventType === CameraTrailAuditEventType.CREATE
                                                  ? newVal
                                                  : oldVal
                                          }"`;
                                } catch {
                                    return '';
                                }
                            })()}`}
                        </Typography>
                    </li>
                </ul>
            ) : isTaskTag ? (
                <ul
                    style={{
                        marginTop: '4px',
                        marginBottom: '4px',
                    }}
                >
                    <li>
                        <Typography component="div">
                            {`${change.key}:`}
                            <ul>
                                {item.AuditEventType === CameraTrailAuditEventType.UPDATE ? (
                                    <>
                                        <li>
                                            <Typography>From:</Typography>
                                            <ul>
                                                {JSON.parse(change.oldValue !== 'N/A' ? change.oldValue : '[]')?.map(
                                                    (task: CameraSubTasks, index: number) => (
                                                        <li key={`old-${index}`}>
                                                            {task.Name}.{' '}
                                                            {task.CompletedAt &&
                                                                ` - ${formatUKDateTime(task.CompletedAt)}.`}{' '}
                                                            ({task.Status})
                                                        </li>
                                                    )
                                                ) ?? <li>-</li>}
                                            </ul>
                                        </li>
                                        <li>
                                            <Typography>To:</Typography>
                                            <ul>
                                                {JSON.parse(change.newValue !== 'N/A' ? change.newValue : '[]')?.map(
                                                    (task: CameraSubTasks, index: number) => (
                                                        <li key={`new-${index}`}>
                                                            {task.Name}.{' '}
                                                            {task.CompletedAt &&
                                                                ` - ${formatUKDateTime(task.CompletedAt)}.`}{' '}
                                                            ({task.Status})
                                                        </li>
                                                    )
                                                )}
                                            </ul>
                                        </li>
                                    </>
                                ) : (
                                    <ul>
                                        {JSON.parse(
                                            change.oldValue !== 'N/A'
                                                ? change.oldValue
                                                : change.newValue !== 'N/A'
                                                ? change.newValue
                                                : '[]'
                                        ).map((task: CameraSubTasks, index: number) => (
                                            <li key={`single-${index}`}>
                                                {task.Name}.{' '}
                                                {task.CompletedAt && ` - ${formatUKDateTime(task.CompletedAt)}.`} (
                                                {task.Status})
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </ul>
                        </Typography>
                    </li>
                </ul>
            ) : isSolar4gCellularInfo ? (
                <ul
                    style={{
                        marginTop: '4px',
                        marginBottom: '4px',
                    }}
                >
                    <li>
                        <Typography>
                            {change.key}: {change.newValue}
                        </Typography>
                    </li>
                </ul>
            ) : isSolar4gCellularStatus ? (
                <ul
                    style={{
                        marginTop: '4px',
                        marginBottom: '4px',
                    }}
                >
                    <li>
                        <Typography>
                            {change.key}: {change.newValue}
                        </Typography>
                    </li>
                </ul>
            ) : isSolar4gCaptureInfo ? (
                <ul
                    style={{
                        marginTop: '4px',
                        marginBottom: '4px',
                    }}
                >
                    <li>
                        <Typography>
                            {change.key}: {change.newValue}
                        </Typography>
                    </li>
                </ul>
            ) : change.key === EV_PERMIT_ZONES_LABEL ? (
                <EvPermitZonesChange label={change.key} oldValue={change.oldValue} newValue={change.newValue} />
            ) : isExemptVehicleTypes ? (
                (() => {
                    const oldFormatted = formatCommaSeparatedValues(change.oldValue);
                    const newFormatted = formatCommaSeparatedValues(change.newValue);

                    let display: string | null;
                    switch (item?.AuditEventType) {
                        case CameraTrailAuditEventType.CREATE:
                            display = newFormatted || null;
                            break;
                        case CameraTrailAuditEventType.DELETE:
                            display = oldFormatted || null;
                            break;
                        default:
                            display =
                                oldFormatted || newFormatted
                                    ? `from “${oldFormatted || 'N/A'}” to “${newFormatted || 'N/A'}”`
                                    : null;
                    }

                    if (!display) return null;

                    return (
                        <ul
                            style={{
                                marginTop: '4px',
                                marginBottom: '4px',
                            }}
                        >
                            <li>
                                <Typography>
                                    {change.key}: {display}
                                </Typography>
                            </li>
                        </ul>
                    );
                })()
            ) : (
                <ul
                    style={{
                        marginTop: '4px',
                        marginBottom: '4px',
                    }}
                >
                    <li>
                        <Typography>
                            {change.key}:{' '}
                            {(() => {
                                switch (item?.AuditEventType) {
                                    case CameraTrailAuditEventType.CREATE:
                                        return change.newValue ?? '';
                                    case CameraTrailAuditEventType.DELETE:
                                        return !_.isNil(change.oldValue) ? change.oldValue : 'N/A';
                                    default:
                                        return `from “${!_.isNil(change.oldValue) ? change.oldValue : 'N/A'}” to “${
                                            change.newValue ?? ''
                                        }”`;
                                }
                            })()}
                        </Typography>
                    </li>
                </ul>
            )}
        </Box>
    );
};

export const renderSection = (
    label: string,
    item: CameraTrailAuditEvent,
    handleClickNavigateTaskScreen: (item: CameraTrailAuditEvent) => void,
    changes?: checkValues[],
    keys?: string[],
    handleClickNavigateGeoZone?: (item: CameraTrailAuditEvent) => void,
    allGeoZones?: GeoZones[]
) => {
    return (
        keyExistsInList(keys!, changes) &&
        (item.EntityName != CameraTrailAuditEventEntityName.ptzPreset
            ? renderPrefixText(label, item, handleClickNavigateTaskScreen, handleClickNavigateGeoZone, allGeoZones)
            : renderPrefixPresetText(label, item))
    );
};

export const getGeoZoneDisplayName = (item: CameraTrailAuditEvent, allGeoZones?: GeoZones[]): string => {
    const name =
        (item.NewValues as GeoZoneAuditEvent)?.GeoZoneName ||
        (item.OldValues as GeoZoneAuditEvent)?.GeoZoneName;
    if (name) return name;

    if (item.Key && allGeoZones) {
        const found = allGeoZones.find((gz) => gz.Id === item.Key);
        if (found?.GeoZoneName) return found.GeoZoneName;
    }

    return item.Key ? `GeoZone #${item.Key}` : '';
};

export const renderPrefixText = (
    prefixText: string,
    item: CameraTrailAuditEvent,
    handleClickNavigateTaskScreen: (item: CameraTrailAuditEvent) => void,
    handleClickNavigateGeoZone?: (item: CameraTrailAuditEvent) => void,
    allGeoZones?: GeoZones[]
) => {
    const geoZoneName =
        item.EntityName === CameraTrailAuditEventEntityName.geoZones
            ? getGeoZoneDisplayName(item, allGeoZones)
            : '';

    return (
        <Stack
            direction={'row'}
            spacing={
                item.CameraTask?.TaskName !== undefined || geoZoneName ? 0.5 : 0
            }
            alignItems={'center'}
        >
            <Typography>
                {`${item.UpdatedBy ?? ''} ${formatEnumValue({
                    enumKey: item.AuditEventType,
                    enumMapping: eventLogType,
                    capitalizeFirstLetter: false,
                })} ${prefixText}`}
            </Typography>
            {item.EntityName === CameraTrailAuditEventEntityName.cameraTask && (
                <Typography
                    sx={{
                        color: color.secondary,
                        cursor: 'pointer',
                    }}
                    onClick={() => handleClickNavigateTaskScreen(item)}
                >
                    {item.CameraTask?.TaskName !== undefined ? `${item.CameraTask?.TaskName}:` : ''}
                </Typography>
            )}
            {item.EntityName === CameraTrailAuditEventEntityName.geoZones &&
                item.AuditEventType !== CameraTrailAuditEventType.DELETE &&
                item.Key &&
                handleClickNavigateGeoZone ? (
                <Typography
                    sx={{
                        color: color.secondary,
                        cursor: 'pointer',
                    }}
                    onClick={() => handleClickNavigateGeoZone(item)}
                >
                    {geoZoneName ? `${geoZoneName}:` : ''}
                </Typography>
            ) : item.EntityName === CameraTrailAuditEventEntityName.geoZones && geoZoneName ? (
                <Typography>{`${geoZoneName}:`}</Typography>
            ) : null}
        </Stack>
    );
};

const renderPrefixPresetText = (prefixText: string, item: CameraTrailAuditEvent) => (
    <Typography>{`${item.UpdatedBy ?? ''} detected change in the ${item.Notes} ${prefixText}:`}</Typography>
);

export const sections = (entityName: string, auditEventType: CameraTrailAuditEventType): Section[] => {
    return [
        {
            label: 'camera details:',
            keys: [
                'Camera name',
                'Mac',
                'Asset',
                'Len caps',
                'Status',
                'Purpose',
                'Solar Panel Position',
                'Power supply',
            ],
            condition: entityName === CameraTrailAuditEventEntityName.camera,
        },
        {
            label: '4G cellular status:',
            keys: [
                'ISP',
                'Connection status',
                'SIM status',
                'Signal strength',
                'IMEI',
                'IMSI',
                'ICCID',
                'IP address',
                'Subnet mask',
                'Gateway',
                'DNS',
                'Monthly data',
            ],
            condition: entityName === CameraTrailAuditEventEntityName.solar4gCamera,
        },
        {
            label: '4G cellular info:',
            keys: ['APN', 'Password', 'Username'],
            condition: entityName === CameraTrailAuditEventEntityName.solar4gCamera,
        },
        {
            label: '4G capture info:',
            keys: [
                'Snapshot topic',
                'Status topic',
                'Alarm topic',
                'Configure request topic',
                'Configure respond topic',
            ],
            condition: entityName === CameraTrailAuditEventEntityName.solar4gCamera,
        },
        {
            label: 'service:',
            keys: ['ANPR ID', 'Service'],
        },
        {
            label: 'camera login information:',
            keys: ['Password', 'IP', 'Username'],
            condition: entityName !== CameraTrailAuditEventEntityName.solar4gCamera,
        },
        {
            label: 'geo-zones:',
            keys: [
                'Effective from',
                'Contravention type',
                'Validation type',
                'Permit exceptions',
                'Coordinates',
                'Status',
                'Geo-zone name',
                'Total bay',
                auditEventType !== CameraTrailAuditEventType.DELETE ? 'Geo-zone configs' : '',
                'Extra night shot',
            ],
            condition: entityName === CameraTrailAuditEventEntityName.geoZones,
        },
        {
            label: 'allocated zone:',
            keys: ['Location', 'Zone', 'Time zone'],
        },
        {
            label: 'production:',
            keys: ['Production'],
        },
        {
            label: 'data processing:',
            keys: [
                'Enable verifications at',
                'Enable verifications',
                'Enable group matching',
                'Enable group matching at',
                'Patrol mode',
            ],
        },
        {
            label: 'EV permit exemption:',
            keys: ['EV permit exemption', 'EV permit length', 'EV permit zones'],
        },
        {
            label:
                entityName === CameraTrailAuditEventEntityName.notes
                    ? 'verifier note:'
                    : entityName === CameraTrailAuditEventEntityName.cameraNotes
                    ? 'camera note:'
                    : '',
            keys: ['Notes', 'Exempt vehicle types'],
            condition:
                entityName === CameraTrailAuditEventEntityName.notes ||
                entityName === CameraTrailAuditEventEntityName.cameraNotes,
        },
        {
            label: 'task',
            keys: [
                'Task Name',
                'Priority',
                'Status',
                'Assignee',
                'Problem',
                'Solution',
                'Task Type',
                'Sub task',
                'Note',
                'Due Date',
            ],
            condition: entityName === CameraTrailAuditEventEntityName.cameraTask,
        },
        {
            label: 'preset',
            keys: ['Preset name', 'Pan', 'Tilt', 'Zoom'],
            condition: entityName === CameraTrailAuditEventEntityName.ptzPreset,
        },
    ];
};

export const AuditTableCell: React.FC<{
    align: 'left' | 'right' | 'center' | 'justify' | 'inherit';
    changes?: checkValues[];
    item: CameraTrailAuditEvent;
    entityName: string;
    handleClickNavigateTaskScreen: (item: CameraTrailAuditEvent) => void;
    handleClickNavigateGeoZone?: (item: CameraTrailAuditEvent) => void;
    allGeoZones?: GeoZones[];
}> = ({ align, changes, item, entityName, handleClickNavigateTaskScreen, handleClickNavigateGeoZone, allGeoZones }) => {
    const renderSectionsAndItems = () => {
        return sections(entityName, item.AuditEventType).flatMap((section) => {
            if (section.condition === false) {
                return [];
            }
            const sectionLabel = renderSection(
                section.label,
                item,
                handleClickNavigateTaskScreen,
                changes,
                section.keys,
                handleClickNavigateGeoZone,
                allGeoZones
            );
            const sectionItems = changes
                ?.filter((change) => section.keys.includes(change.key))
                .map((change) => renderChangeItem(change, item));
            return sectionLabel && sectionItems ? [sectionLabel, sectionItems] : [];
        });
    };

    return (
        <TableCell align={align}>
            {renderSectionsAndItems().map((element, index) => (
                <Fragment key={index}>{element}</Fragment>
            ))}
            {item.EntityName != CameraTrailAuditEventEntityName.ptzPreset && item.Notes && (
                <Typography>{`${item.UpdatedBy ?? ''} ${item.Notes}`}</Typography>
            )}
        </TableCell>
    );
};
